// Retention Console — cohort, curve, NPS, journey, peak (now-working admin RPCs).
import React, { useState, useEffect } from 'react';
import { ConsoleShell, KPI, Panel, SectionLabel, Bar, StatLine, Empty, Table, useConsoleTheme, useIsMobile } from '../components/Console';
import { supabase } from '../supabaseClient';

const fmt = n => (n == null ? '—' : Number(n).toLocaleString());
function useRpc(name) {
  const [d, setD] = useState(null);
  useEffect(() => { let c = false; supabase.rpc(name).then(({ data }) => { if (!c) setD(data); }); return () => { c = true; }; }, [name]);
  return d;
}
function useHub(id) {
  const [d, setD] = useState(null);
  useEffect(() => { let c = false; supabase.rpc('admin_get_hub_overview', { p_hub_id: id }).then(({ data }) => { if (!c) setD(data && data.metrics); }); return () => { c = true; }; }, [id]);
  return d;
}

function Body() {
  const { t } = useConsoleTheme();
  const mobile = useIsMobile();
  const hub = useHub('retention');
  const ret = useRpc('admin_retention_curve');
  const coh = useRpc('admin_cohort_overview');
  const nps = useRpc('admin_nps_overview');
  const jou = useRpc('admin_user_journey');
  const peak = useRpc('admin_peak_activity');
  const curve = (ret && ret.curve) || [];
  const maxCurve = Math.max(1, ...curve.map(c => c.active_pct));
  const steps = (jou && jou.steps) || [];
  const maxStep = Math.max(1, ...steps.map(s => s.count));
  const cohorts = (coh && coh.cohorts) || [];
  const byHour = (peak && peak.by_hour) || [];
  const maxHour = Math.max(1, ...byHour.map(h => h.count));
  const col = n => (mobile ? '1fr 1fr' : `repeat(${n},1fr)`);
  const m = hub || [];
  const fallback = [{ label:'Active passes' }, { label:'Lifetime sold' }, { label:'Refunded' }, { label:'Expiring 7d' }];
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns: col(4), gap:10 }}>
        {(m.length ? m : fallback).slice(0, 4).map((k, i) => <KPI key={i} label={k.label} value={k.value != null ? fmt(k.value) : '…'} sub={k.sub} accent={k.accent} />)}
      </div>

      <SectionLabel>Retention</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:12 }}>
        <Panel title="◠ Retention curve" right="% active in week N">
          <StatLine label="Total signups (180d)" value={ret ? fmt(ret.total_signups) : '…'} />
          <div style={{ height:1, background:t.bd, margin:'10px 0' }} />
          {curve.length === 0 ? <Empty>No active-user data in the cohort yet.</Empty>
            : curve.map(c => <Bar key={c.week} label={`W${c.week}`} value={`${c.active_pct}%`} pct={Math.round(c.active_pct / maxCurve * 100)} color={t.up} labelWidth={42} />)}
        </Panel>
        <Panel title="◇ User journey funnel">
          {steps.length === 0 ? <Empty>No journey events yet.</Empty>
            : steps.map(s => <Bar key={s.step} label={s.step} value={fmt(s.count)} pct={Math.round(s.count / maxStep * 100)} labelWidth={120} />)}
        </Panel>
      </div>

      <SectionLabel>Cohorts & feedback</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1.4fr 1fr', gap:12 }}>
        <Panel title="◈ Weekly cohorts">
          <Table empty="No sign-up cohorts in the last 180 days yet."
            cols={[
              { label:'Week', key:'w', muted:true },
              { label:'Signups', key:'s', align:'right', num:true },
              { label:'W1', key:'w1', align:'right', num:true },
              { label:'W4', key:'w4', align:'right', num:true },
            ]}
            rows={cohorts.map(c => ({ w:c.week_start, s:fmt(c.signups), w1: c.signups ? Math.round(100 * c.retained_w1 / c.signups) + '%' : '—', w4: c.signups ? Math.round(100 * c.retained_w4 / c.signups) + '%' : '—' }))} />
        </Panel>
        <Panel title="◔ NPS / would-return">
          {!nps ? <Empty>Loading…</Empty> : (
            <>
              <StatLine label="Total responses" value={fmt(nps.total_responses)} />
              <StatLine label="NPS-like score" value={nps.nps_score} color={nps.nps_score >= 30 ? t.up : nps.nps_score >= 0 ? t.warn : t.danger} />
              <StatLine label="Would return (yes)" value={fmt((nps.distribution && nps.distribution.yes) || 0)} color={t.up} />
              {!nps.total_responses && <div style={{ marginTop:8 }}><Empty>From verified-trip inscriptions — populates after the first review.</Empty></div>}
            </>
          )}
        </Panel>
      </div>

      <SectionLabel>Peak activity</SectionLabel>
      <Panel title="◷ Events by hour (UTC) · 7d" right={peak ? `${fmt(peak.total_events_7d)} events` : ''}>
        {byHour.length === 0 ? <Empty>No events in the last 7 days yet.</Empty>
          : (
            <div style={{ display:'flex', alignItems:'flex-end', gap:3, height:120 }}>
              {Array.from({ length:24 }).map((_, h) => {
                const row = byHour.find(x => x.hour === h); const v = row ? row.count : 0;
                return (
                  <div key={h} style={{ flex:1, textAlign:'center' }}>
                    <div style={{ background:t.up, height:`${Math.round(v / maxHour * 100)}%`, minHeight: v ? 2 : 0, borderRadius:'2px 2px 0 0' }} title={`${h}:00 — ${v}`} />
                    <div style={{ fontSize:'.5rem', color:t.mut, marginTop:2 }}>{h % 6 === 0 ? h : ''}</div>
                  </div>
                );
              })}
            </div>
          )}
      </Panel>
    </div>
  );
}

export default function RetentionConsole() {
  return <ConsoleShell title="Retention Console"><Body /></ConsoleShell>;
}
